import * as turf from "@turf/turf";
import { LngLatLike } from "mapbox-gl";
import { GlobalStoreContextType } from "../context/GlobalStoreContext";
import { Area } from "./Area";
import { Equipment } from "./Equipment";
import { geometry, MapObjectModel } from "./MapObject.model";

export class MapObject extends MapObjectModel {
    // TODO: We are still using the non-generated model version of equipment
    getMappable(globalStoreContext: GlobalStoreContextType): Area | Equipment | null {
        if (this.mappableType === "Area") {
            const projectAreas = globalStoreContext.mapAreasForProject(this.projectId);
            const area = projectAreas.find((area) => area.id === this.mappableId);

            if (area) {
                return area;
            }
        } else if (this.mappableType === "Equipment") {
            const projectEquipments = globalStoreContext.equipmentForProject(this.projectId);
            const equipment = projectEquipments.find((equipment) => equipment.id === this.mappableId);

            if (equipment) {
                return equipment;
            }
        }

        return null;
    }

    mapBoxGeometry(): GeoJSON.Geometry["type"] {
        if (this.geometry === "point") {
            return "Point" as GeoJSON.Geometry["type"];
        }
        if (this.geometry === "lineString") {
            return "LineString" as GeoJSON.Geometry["type"];
        }
        if (this.geometry === "polygon") {
            return "Polygon" as GeoJSON.Geometry["type"];
            // } else if (this.geometry.type === "MultiPoint") {
            //     return "MultiPoint";
            // } else if (this.geometry.type === "MultiLineString") {
            //     return "MultiLineString";
            // } else if (this.geometry.type === "MultiPolygon") {
            //     return "MultiPolygon";
        } else {
            throw new Error("Unsupported geometry type");
        }
    }
    mapboxIconAnchor(): mapboxgl.Anchor {
        switch (this.iconAnchor) {
            case "center":
                return "center";
            case "left":
                return "left";
            case "right":
                return "right";
            case "top":
                return "top";
            case "bottom":
                return "bottom";
            case "top_left":
                return "top-left";
            case "top_right":
                return "top-right";
            case "bottom_left":
                return "bottom-left";
            case "bottom_right":
                return "bottom-right";
            default:
                throw new Error("Unsupported icon anchor type");
        }
    }

    static fromFeature(feature: GeoJSON.Feature, project, mappableType: "area" | "equipment"): MapObject {
        const defaultIcon = (geometry: string | geometry): string | null => {
            switch (geometry.toLowerCase()) {
                case "point":
                    return "bullet_sdf_icon";
                case "polygon":
                    return null; // No default icon for polygons, they will use the fill color
                case "linestring":
                    return "arrow_sdf_icon";
                default:
                    return "bullet_sdf_icon";
            }
        };

        return MapObject.fromApiObject(
            {
                color: "#" + ((Math.random() * 0xffffff) << 0).toString(16).padStart(6, "0"),
                outline: feature.geometry.type === "Polygon" ? "#000000" : null,
                opacity: feature.geometry.type === "Polygon" ? 0.6 : 1,
                geometry: (feature.geometry.type.charAt(0).toLowerCase() + feature.geometry.type.slice(1)) as geometry,
                coordinates: feature.geometry.coordinates,
                icon: mappableType == "equipment" ? "tower_crane_orange" : defaultIcon(feature.geometry.type),
                icon_anchor: "center",
                icon_units: null,
                icon_width: null,
                mappable_id: -1,
                mappable_type: mappableType == "area" ? "Area" : "Equipment",
                project_id: project.id,
                uuid: feature.id,
                archived: false,
            },
            project.timezone
        );
    }

    getCenter(): LngLatLike {
        switch (this.geometry) {
            case "lineString": {
                const midpointIndex = Math.round((this.coordinates.length - 1) / 2);
                const midpoint = this.coordinates[midpointIndex];
                return midpoint;
            }
            case "polygon": {
                const polygon = turf.polygon(this.coordinates);
                const centroid = turf.centroid(polygon);
                return centroid.geometry.coordinates as LngLatLike;
            }
            default:
                return this.coordinates;
        }
    }

    toFeature(): GeoJSON.Feature {
        return {
            id: this.uuid,
            type: "Feature",
            properties: {
                uuid: this.uuid,
                id: this.uuid,
                icon: this.icon,
                iconAnchor: this.iconAnchor,
                iconWidth: this.iconWidth,
                iconUnits: this.iconUnits,
                color: this.color,
                opacity: this.opacity,
                mappableId: this.mappableId,
                mappableType: this.mappableType,
                tolerance: 0,
            },
            geometry: {
                type: this.mapBoxGeometry(),
                coordinates: this.coordinates,
            },
        };
    }

    setVisibility(map: mapboxgl.Map, visibility: "visible" | "none") {
        switch (this.geometry) {
            case "point":
                if (map.getLayer(this.uuid)) {
                    map.setLayoutProperty(this.uuid, "visibility", visibility);
                }
                break;
            case "lineString":
                if (map.getLayer(this.uuid)) {
                    map.setLayoutProperty(this.uuid, "visibility", visibility);
                }
                if (map.getLayer(`${this.uuid}_line_icon`)) {
                    map.setLayoutProperty(`${this.uuid}_line_icon`, "visibility", visibility);
                }
                if (map.getLayer(`${this.uuid}_lineCaps`)) {
                    map.setLayoutProperty(`${this.uuid}_lineCaps`, "visibility", visibility);
                }
                break;
            case "polygon":
                if (map.getLayer(this.uuid)) {
                    map.setLayoutProperty(this.uuid, "visibility", visibility);
                }
                if (map.getLayer(`${this.uuid}_outline`)) {
                    map.setLayoutProperty(`${this.uuid}_outline`, "visibility", visibility);
                }
                if (map.getLayer(`${this.uuid}_outline_icon`)) {
                    map.setLayoutProperty(`${this.uuid}_outline_icon`, "visibility", visibility);
                }
                break;
            default:
                throw new Error("Unsupported geometry type for toggling visibility");
        }

        if (this.radiuses && this.radiuses?.length > 0) {
            this.radiuses.forEach((radius) => {
                radius.toggleVisibility(map, this, visibility);
            });
        }
    }

    removeLayerFromMapbox(map: mapboxgl.Map) {
        if (map.getLayer(this.uuid)) {
            map.removeLayer(this.uuid);
        }
        if (this.geometry == "polygon") {
            if (map.getLayer(`${this.uuid}_outline`)) {
                map.removeLayer(`${this.uuid}_outline`);
            }
            if (map.getLayer(`${this.uuid}_outline_icon`)) {
                map.removeLayer(`${this.uuid}_outline_icon`);
            }
        }
        if (this.geometry == "lineString") {
            if (map.getLayer(`${this.uuid}_line_icon`)) {
                map.removeLayer(`${this.uuid}_line_icon`);
            }
            if (map.getLayer(`${this.uuid}_lineCaps`)) {
                map.removeLayer(`${this.uuid}_lineCaps`);
            }
        }
        if (this.radiuses && this.radiuses?.length > 0) {
            this.radiuses.forEach((radius) => {
                radius.removeLayerFromMapbox(map, this);
            });
        }
    }
}
